import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useOrderComponents, OrderComponent } from '@/hooks/useOrderComponents';
import { supabase } from '@/lib/supabase';

// ── Landscape A5-ish certificate ────────────────────────────────

const TYPE_LABELS: Record<string, string> = {
  cpu: 'CPU', cpu_cooler: 'Cooler', fan: 'Cooler', coo: 'Cooler',
  mb: 'MB', ram: 'RAM',
  rom_1: 'Storage', rom_2: 'Storage', rom_3: 'Storage', storage: 'Storage', rom: 'Storage',
  gpu: 'GPU', psu: 'PSU', os: 'OS', case: 'Case', cas: 'Case',
};
function catLabel(t: string): string {
  const lower = t?.toLowerCase() ?? '';
  if (TYPE_LABELS[lower]) return TYPE_LABELS[lower];
  if (lower.startsWith('acc')) return 'Accessory';
  return t || '—';
}

import { getComponentSortRank } from '@/types/database';

function componentSortKey(type: string): number {
  return getComponentSortRank(type ?? '');
}

export interface BenchmarkScores {
  passmark_rating: number;
  cpu_mark?: number | null;
  graphics_3d_mark?: number | null;
  memory_mark?: number | null;
  disk_mark?: number | null;
}

interface Props {
  order: {
    id?: string;
    order_number?: string;
    build_sku?: string;
    quantity?: number;
    tech_signature_url?: string | null;
    completed_date?: string | null;
    completed_at?: string | null;
    started_at?: string | null;
    created_at?: string | null;
    build?: { id?: string; sku?: string; description?: string } | null;
    reservation?: { id?: string } | null;
  };
  components?: OrderComponent[];
  serialNumbers?: Record<string, string>;
  serialNumber?: string | null;
  /** Full benchmark objects per unit: { "1": { passmark_rating, cpu_mark, … } } */
  benchmarks?: Record<string, BenchmarkScores>;
  /** Legacy: per-unit PassMark ratings */
  passmarkRatings?: Record<string, number>;
  /** Legacy: single PassMark rating */
  passmarkRating?: number | null;
  signatureUrl?: string | null;
  /** Restrict printing to these unit keys (e.g. ["2"]) instead of all units in the order. */
  unitsToPrint?: string[];
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'icon';
  className?: string;
  disabled?: boolean;
}

export function PrintBuildCertificate({
  order,
  components: externalComponents,
  serialNumbers,
  serialNumber,
  benchmarks: benchmarksProp,
  passmarkRatings,
  passmarkRating,
  signatureUrl,
  unitsToPrint,
  variant = 'outline',
  size = 'sm',
  className,
  disabled = false,
}: Props) {
  const [printing, setPrinting] = useState(false);

  const { data: fetchedComponents, isLoading: loadingComponents } = useOrderComponents(
    externalComponents ? null : order,
  );
  const components = externalComponents || fetchedComponents || [];
  // Print should be blocked while components are still loading and when the
  // resolved list is empty (otherwise we'd print an empty parts table).
  const componentsReady = !loadingComponents && components.length > 0;

  const componentSkus = useMemo(() => components.map(c => c.sku).filter(Boolean), [components]);
  const { data: displayNames = {} } = useQuery({
    queryKey: ['cert-display-names', componentSkus],
    queryFn: async () => {
      if (componentSkus.length === 0) return {};
      const { data } = await supabase
        .from('component_display_names')
        .select('part_number, display_name_b2c')
        .in('part_number', componentSkus);
      const map: Record<string, string> = {};
      (data ?? []).forEach((r: any) => {
        if (r.display_name_b2c) map[r.part_number] = r.display_name_b2c;
      });
      return map;
    },
    enabled: componentSkus.length > 0,
  });

  const sku = order.build?.sku || order.build_sku || '—';
  const description = order.build?.description || '';
  const sigUrl = signatureUrl || order.tech_signature_url || null;
  const dateStr = order.completed_date || order.completed_at || order.started_at || order.created_at || '';
  const formattedDate = dateStr
    ? new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';

  const resolvedSns: Record<string, string> = serialNumbers
    ? serialNumbers
    : serialNumber
      ? { '1': serialNumber }
      : (order as any).serial_numbers
        ? (order as any).serial_numbers
        : (order as any).serial_number
          ? { '1': (order as any).serial_number }
          : {};

  const qty = Math.max(order.quantity || 1, 1);
  const unitKeys = unitsToPrint && unitsToPrint.length > 0
    ? unitsToPrint
    : Array.from({ length: qty }, (_, i) => String(i + 1));

  const getBenchmark = (unitKey: string): BenchmarkScores | null => {
    if (benchmarksProp?.[unitKey]) return benchmarksProp[unitKey];
    // fallback to legacy props
    const rating = passmarkRatings?.[unitKey] ?? passmarkRating;
    if (rating != null) return { passmark_rating: rating };
    return null;
  };

  // Sort components: ACC* types go to the bottom
  const sortedComponents = useMemo(
    () => [...components].sort((a, b) => componentSortKey(a.type) - componentSortKey(b.type)),
    [components],
  );

  const handlePrint = () => {
    if (!componentsReady) {
      toast.error(
        loadingComponents
          ? 'Components are still loading — please try again in a moment.'
          : 'No components found for this order — cannot print certificate.',
      );
      return;
    }
    setPrinting(true);

    // Scale font/padding to fit all components. Base: 10 rows at 9.5px/1.5px padding.
    const rowCount = sortedComponents.length;
    const baseFont = 9.5;
    const basePad = 1.5;
    const basePnFont = 9;
    let fontSize = baseFont;
    let padSize = basePad;
    let pnFontSize = basePnFont;
    if (rowCount > 10) {
      const scale = 10 / rowCount;
      fontSize = Math.max(baseFont * scale, 6.5);
      padSize = Math.max(basePad * scale, 0.5);
      pnFontSize = Math.max(basePnFont * scale, 6);
    }

    // `client` copy (goes in the customer's box) hides the part-number column.
    const buildRows = (client: boolean) => sortedComponents.map(c =>
      `<tr>
        <td class="col-cat">${catLabel(c.type)}</td>
        ${client ? '' : `<td class="col-pn">${c.sku}</td>`}
        <td class="col-desc">${displayNames[c.sku] || c.displayName || c.description || '—'}</td>
        <td class="col-qty">${c.qty}</td>
      </tr>`
    ).join('');

    const buildPage = (unitKey: string, client: boolean) => {
      const componentRows = buildRows(client);
      const sn = resolvedSns[unitKey] || '';
      const bench = getBenchmark(unitKey);

      const subScoresHtml = bench ? `
        <div class="apm-banner">
          <div class="apm-main">
            <div class="apm-main-label">AOI Performance Metric</div>
            <div class="apm-main-score">${bench.passmark_rating.toLocaleString()}</div>
          </div>
          <div class="apm-subs">
            ${bench.cpu_mark != null ? `<div class="apm-sub"><span class="apm-sub-label">CPU Mark</span><span class="apm-sub-score">${bench.cpu_mark.toLocaleString()}</span></div>` : ''}
            ${bench.graphics_3d_mark != null ? `<div class="apm-sub"><span class="apm-sub-label">3D Graphics</span><span class="apm-sub-score">${bench.graphics_3d_mark.toLocaleString()}</span></div>` : ''}
            ${bench.memory_mark != null ? `<div class="apm-sub"><span class="apm-sub-label">Memory</span><span class="apm-sub-score">${bench.memory_mark.toLocaleString()}</span></div>` : ''}
            ${bench.disk_mark != null ? `<div class="apm-sub"><span class="apm-sub-label">Disk</span><span class="apm-sub-score">${bench.disk_mark.toLocaleString()}</span></div>` : ''}
          </div>
        </div>
      ` : '';

      return `
      <div class="label-page${client ? ' client-copy' : ''}">
        <!-- Top 65%: header + table -->
        <div class="top-section">
          <div class="header">
            <img src="/logo-horizontal.png" alt="AOI" class="logo" />
            <div class="header-info">
              <div class="header-line header-sku">${sku}</div>
              <div class="header-line header-sn"><span class="sn-label">S/N:</span> ${sn || '—'}</div>
              <div class="header-line header-desc">${description || ''}</div>
            </div>
          </div>
          <hr />
          <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Cat</th>
                ${client ? '' : '<th>Part No.</th>'}
                <th>Description</th>
                <th>Qty</th>
              </tr>
            </thead>
            <tbody>
              ${componentRows}
            </tbody>
          </table>
          </div>
        </div>
        <!-- Bottom 35%: benchmark + signature -->
        <div class="bottom-section">
          <hr />
          ${subScoresHtml}
          <div class="footer">
            <div class="footer-left">
              <div class="sig-label">Technician</div>
              ${sigUrl ? `
                <img src="${sigUrl}" alt="Signature" class="sig-img" />
              ` : `
                <div class="sig-line"></div>
              `}
            </div>
            ${client ? `
              <div class="footer-right">
                <div class="stamp">
                  <div class="stamp-top">Quality Test</div>
                  <div class="stamp-main">Passed</div>
                </div>
              </div>
            ` : `<div class="footer-right">Build Date: ${formattedDate}</div>`}
          </div>
        </div>
      </div>`;
    };

    // Each unit prints a technician copy followed by a client (box) copy.
    const pages = unitKeys.flatMap(unitKey => [buildPage(unitKey, false), buildPage(unitKey, true)]).join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <title>Build Certificate — ${sku}</title>
  <style>
    @page {
      size: 164mm 103mm;
      margin: 1.5mm 7mm;
    }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 9px;
      color: #111;
      width: 150mm;
      max-width: 150mm;
      overflow: hidden;
    }
    .label-page {
      page-break-after: always;
      display: flex;
      flex-direction: column;
      height: 100mm;
    }
    .label-page:last-child { page-break-after: auto; }
    .top-section {
      display: flex;
      flex-direction: column;
      flex: 1;
      overflow: hidden;
    }
    .bottom-section {
      flex-shrink: 0;
    }

    /* ── Header ──────────────────────────── */
    .header {
      display: flex;
      align-items: stretch;
      gap: 10px;
      margin-bottom: 4px;
    }
    .logo {
      display: block;
      height: 54px;
      width: auto;
      flex-shrink: 0;
    }
    .header-info {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      flex: 1;
      min-width: 0;
      padding: 0;
      text-align: right;
    }
    .header-line { line-height: 1.25; }
    .header-sku { font-family: monospace; font-weight: 700; font-size: 16px; }
    .header-sn { font-size: 12px; font-family: monospace; }
    .sn-label { font-size: 10px; color: #666; }
    .header-desc { font-size: 12px; color: #444; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

    hr { border: none; border-top: 2px solid #999; margin: 2px 0; }

    /* ── Component table ─────────────────── */
    .table-wrap { flex: 1; overflow: hidden; }
    table { width: 100%; border-collapse: collapse; }
    thead th {
      font-size: 9px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: #555;
      padding: 2px 3px;
      border-bottom: 2px solid #999;
      text-align: left;
    }
    tbody td { padding: ${padSize}px 3px; font-size: ${fontSize}px; }
    tbody tr { border-bottom: 1.5px solid #bbb; }
    .col-cat { width: 42px; white-space: nowrap; }
    .col-pn { width: 84px; font-family: monospace; font-size: ${pnFontSize}px; white-space: nowrap; }
    .col-desc { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .col-qty { width: 22px; text-align: center; }
    thead th:first-child { width: 42px; }
    thead th:nth-child(2) { width: 84px; }
    thead th:last-child { width: 22px; text-align: center; }

    /* ── Benchmark banner ────────────────── */
    .apm-banner {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background: #f5f5f5;
      border-radius: 3px;
      padding: 4px 12px;
      margin: 3px 0;
    }
    .apm-main { text-align: center; padding-right: 12px; border-right: 2px solid #bbb; flex-shrink: 0; }
    /* Label is long, so it runs smaller/tighter than the sub-labels to stay on
       one line — wrapping here would grow the banner and steal a table row. */
    .apm-main-label { font-size: 7px; color: #666; text-transform: uppercase; letter-spacing: 0.02em; white-space: nowrap; }
    .apm-main-score { font-family: monospace; font-weight: 700; font-size: 16px; }
    .apm-subs { display: flex; flex: 1; min-width: 0; justify-content: space-evenly; }
    .apm-sub { text-align: center; }
    .apm-sub-label { display: block; font-size: 8px; color: #666; text-transform: uppercase; letter-spacing: 0.03em; }
    .apm-sub-score { display: block; font-family: monospace; font-weight: 600; font-size: 13px; }

    /* ── Footer ──────────────────────────── */
    .footer {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      padding-top: 2px;
      border-top: 2px solid #999;
    }
    .footer-left { text-align: left; }
    .sig-label { font-size: 8px; color: #666; margin-bottom: 1px; }
    .sig-img { height: 64px; width: auto; object-fit: contain; object-position: bottom left; border-bottom: 2px solid #999; display: block; }
    .sig-line { border-bottom: 2px solid #999; width: 160px; height: 44px; }
    .footer-right { text-align: right; font-size: 11px; color: #555; }

    /* ── Client (box) copy ───────────────── */
    /* Part No. column is dropped entirely; description takes the space. */
    .client-copy thead th:nth-child(2) { width: auto; }
    .client-copy thead th:last-child { width: 22px; }
    .client-copy .stamp {
      display: inline-block;
      border: 2.5px solid #1a1a1a;
      border-radius: 4px;
      padding: 2px 12px 3px;
      /* right inset keeps the stamp clear of the printer's edge cut-off */
      margin: 0 12px 4px 0;
      text-align: center;
      color: #1a1a1a;
      opacity: 0.88;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .client-copy .stamp-top {
      font-size: 8px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.18em;
      border-bottom: 1.5px solid currentColor;
      padding-bottom: 1px;
    }
    .client-copy .stamp-main {
      font-size: 19px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      line-height: 1.15;
    }
  </style>
</head>
<body>
  ${pages}
  <script>
    // Crop whitespace from signature images using canvas ink-detection
    function cropSignature(img) {
      try {
        var c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        if (!c.width || !c.height) return;
        var ctx = c.getContext('2d');
        ctx.drawImage(img, 0, 0);
        var d = ctx.getImageData(0, 0, c.width, c.height).data;
        var top = c.height, bot = 0, left = c.width, right = 0;
        for (var y = 0; y < c.height; y++) {
          for (var x = 0; x < c.width; x++) {
            var i = (y * c.width + x) * 4;
            // detect non-white, non-transparent pixels
            if (d[i+3] > 20 && (d[i] < 240 || d[i+1] < 240 || d[i+2] < 240)) {
              if (y < top) top = y;
              if (y > bot) bot = y;
              if (x < left) left = x;
              if (x > right) right = x;
            }
          }
        }
        if (bot <= top) return; // no ink found
        // add small padding
        var pad = 4;
        top = Math.max(0, top - pad);
        bot = Math.min(c.height - 1, bot + pad);
        left = Math.max(0, left - pad);
        right = Math.min(c.width - 1, right + pad);
        var cw = right - left + 1, ch = bot - top + 1;
        var c2 = document.createElement('canvas');
        c2.width = cw; c2.height = ch;
        c2.getContext('2d').drawImage(c, left, top, cw, ch, 0, 0, cw, ch);
        img.src = c2.toDataURL('image/png');
      } catch(e) {}
    }
    document.querySelectorAll('.sig-img').forEach(function(img) {
      if (img.complete && img.naturalWidth) cropSignature(img);
      else img.onload = function() { cropSignature(img); };
    });
  </script>
</body>
</html>`;

    const printWindow = window.open('', '_blank', 'width=660,height=420');
    if (!printWindow) { setPrinting(false); return; }

    printWindow.document.write(html);
    printWindow.document.close();

    const images = printWindow.document.querySelectorAll('img');
    let loaded = 0;
    const total = images.length;
    let printed = false;
    const doPrint = () => {
      if (printed) return;
      printed = true;
      // allow crop script to run before printing
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        setPrinting(false);
      }, 200);
    };
    if (total === 0) {
      setTimeout(doPrint, 100);
    } else {
      images.forEach(img => {
        img.onload = img.onerror = () => { loaded++; if (loaded >= total) setTimeout(doPrint, 250); };
      });
      setTimeout(doPrint, 3000);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      disabled={printing || disabled || !componentsReady}
      onClick={handlePrint}
      title={
        loadingComponents
          ? 'Loading components…'
          : !components.length
            ? 'No components found for this order'
            : undefined
      }
    >
      <Printer className="h-4 w-4 mr-1.5" />
      {printing ? 'Printing…' : `Print Certificate${unitKeys.length > 1 ? ` (${unitKeys.length})` : ''}`}
    </Button>
  );
}
